import { USE_MOCKS, api, descargarArchivo, mockDelay } from "@/lib/api"

/**
 * La planilla del período.
 *
 *   GET /api/payroll/{anio}/{mes}?entity_id
 *
 * El cálculo no vive aquí ni en el endpoint: sale del rulepack de cada país, que
 * es el mismo motor que alimenta los asientos de planilla y el calendario de
 * caja. Ver la cabecera de `app/modules/payroll/router.py`.
 */

export interface ConceptoPlanilla {
  codigo: string
  nombre: string
  /** EARNING | EMPLOYEE_DEDUCTION | EMPLOYER_CONTRIBUTION | PROVISION | WITHHOLDING */
  tipo: string
  /** EMPLOYER | EMPLOYEE */
  paga: string
  importe: number
  fecha_caja: string | null
  /** Se devenga este mes y sale de caja en otro: gratificación, CTS, vacaciones. */
  diferido: boolean
}

export interface PersonaEnPlanilla {
  employee_id: string
  nombres: string
  documento: string | null
  regimen: string
  centro_costo: string | null
  moneda: string
  /** Lo que se le reconoce. */
  devengado: number
  /** Lo que se le retiene (ONP/AFP, renta de 5ta). */
  descuentos: number
  /** Lo que de verdad recibe: devengado − descuentos. */
  neto: number
  /** Lo que el empleador paga ADEMÁS (EsSalud, SCTR) más las provisiones. */
  aportes: number
  /** Lo que le cuesta a la empresa: devengado + aportes. */
  costo_empresa: number
  /** De todo lo anterior, lo que NO sale de caja este mes. */
  diferido: number
  conceptos: ConceptoPlanilla[]
}

export interface Planilla {
  periodo: string
  sociedad: string
  pais: string
  moneda: string
  personas: PersonaEnPlanilla[]
  totales: {
    devengado: number
    descuentos: number
    neto: number
    aportes: number
    costo: number
    diferido: number
  }
  /**
   * Lo que el motor NO pudo afirmar. Se muestra ARRIBA y no en un pie: donde la
   * regla no está verificada los aportes salen en cero, y un cero silencioso en
   * una planilla es peor que una pantalla vacía porque se firma.
   */
  advertencias: string[]
  /** `false` fuera de Perú: el único rulepack laboral completo hoy. */
  reglas_verificadas: boolean
}

const VACIA: Planilla = {
  periodo: "", sociedad: "", pais: "PE", moneda: "PEN", personas: [],
  totales: { devengado: 0, descuentos: 0, neto: 0, aportes: 0, costo: 0, diferido: 0 },
  advertencias: [], reglas_verificadas: false,
}

export async function fetchPlanilla(
  anio: number,
  mes: number,
  entityId: string | null,
): Promise<Planilla> {
  if (USE_MOCKS || !entityId) return mockDelay(VACIA)
  return api.get<Planilla>(`/payroll/${anio}/${mes}`, {
    query: { entity_id: entityId },
  })
}

// ===========================================================================
// LAS BOLETAS EN PDF — punto 10b
// ===========================================================================

/**
 * QUÉ LE FALTA A ESTAS BOLETAS PARA PODER ENTREGARSE, antes de bajarlas.
 *
 * Existe porque la alternativa es que alguien descargue veintiocho páginas,
 * las imprima y descubra la banda roja en la primera. Lo que falta casi
 * siempre es un dato del maestro que se carga en cinco minutos.
 */
export interface EstadoDeBoletas {
  periodo: string
  pais: string
  personas: number
  entregables: number
  borradores: number
  /**
   * AGREGADO Y NO POR PERSONA: casi siempre es el MISMO campo el que le falta
   * a las veintiocho, y una lista de veintiocho filas idénticas esconde que se
   * arregla con un solo dato.
   */
  que_falta: Array<{ texto: string; personas: number }>
  /** `false` fuera de Perú: lo que sale es un detalle, no la boleta legal. */
  documento_legal: boolean
  /** `false` si el servidor no tiene reportlab; se bajará HTML imprimible. */
  formato_pdf: boolean
  nota: string
}

export function fetchEstadoDeBoletas(
  anio: number, mes: number, entityId: string | null,
): Promise<EstadoDeBoletas> {
  return api.get<EstadoDeBoletas>(`/payroll/${anio}/${mes}/boletas/estado`,
                                  { query: { entity_id: entityId } })
}

/**
 * Baja las boletas. Sin `employeeId`, todas en un solo PDF de varias páginas.
 *
 * UN ARCHIVO Y NO UN ZIP: las boletas se imprimen para firmarlas, y un archivo
 * se manda a la impresora de una vez.
 */
export function descargarBoletas(
  anio: number, mes: number, entityId: string,
  opciones: { employeeId?: string; formato?: "pdf" | "html" } = {},
): Promise<void> {
  const query = new URLSearchParams({ entity_id: entityId })
  query.set("formato", opciones.formato ?? "pdf")
  if (opciones.employeeId) query.set("employee_id", opciones.employeeId)
  return descargarArchivo(
    `/payroll/${anio}/${mes}/boletas?${query.toString()}`,
    `boletas-${anio}-${String(mes).padStart(2, "0")}.${opciones.formato ?? "pdf"}`,
  )
}
